import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Milk } from 'lucide-react';
import type { ReactElement } from 'react';

import { Avatar, Icon } from '@/ui/kit';
import { elevation, fontWeights, radius } from '@/ui/tokens';

/**
 * Les trois cartes flottantes du panneau de connexion.
 *
 * **Purement decoratives, et fictives.** « 120 ml », « +2 » et « 1,2 L » ne
 * sont lus nulle part : aucune donnee de l'API ni de l'appareil, aucun nom reel
 * (les initiales viennent de noms inventes). Tout le bloc est donc masque aux
 * technologies d'assistance (`aria-hidden`) — un lecteur d'ecran n'a pas a
 * annoncer des chiffres qui ne veulent rien dire — et aucune interaction ne
 * lui est donnee.
 *
 * Positions en pixels depuis les bords du panneau : les cartes gardent leur
 * taille de lecture quand le panneau retrecit, la ou l'illustration, elle,
 * suit la largeur (`IllustrationPanel`).
 */

const cardSx = {
  position: 'absolute',
  display: 'flex',
  bgcolor: 'tokens.surface',
  boxShadow: (theme: { palette: { mode: 'light' | 'dark' } }) => elevation[theme.palette.mode].overlay,
} as const;

const caption = { fontSize: 12, fontWeight: fontWeights.semiBold, lineHeight: '16px', color: 'tokens.textMuted' };

/** Hauteurs des sept barres de la carte « semaine », la derniere etant « aujourd'hui ». */
const BAR_HEIGHTS = [22, 30, 26, 38, 32, 42, 46] as const;

const FAMILY = [
  { name: 'Léa Martin', tone: 'pink' },
  { name: 'Jules Moreau', tone: 'mint' },
  { name: 'Alice Lambert', tone: 'blue' },
] as const;

export const FloatingCards = (): ReactElement => (
  <Box aria-hidden data-testid="decor-cards">
    <Box
      sx={{
        ...cardSx,
        left: 40,
        top: 150,
        alignItems: 'center',
        gap: 1.5,
        p: 1.75,
        borderRadius: `${String(radius.card + 2)}px`,
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: '12px',
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'tokens.accentPinkSoft',
          color: 'tokens.accentPinkStrong',
        }}
      >
        <Icon icon={Milk} size={20} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 18, fontWeight: fontWeights.extraBold, lineHeight: '22px' }}>120 ml</Typography>
        <Typography sx={caption}>Tirage · 07:40</Typography>
      </Box>
    </Box>

    <Box
      sx={{
        ...cardSx,
        right: 44,
        top: 270,
        alignItems: 'center',
        gap: 1.5,
        py: 1,
        pl: 1,
        pr: 1.75,
        borderRadius: `${String(radius.pill)}px`,
      }}
    >
      <Box sx={{ display: 'flex' }}>
        {FAMILY.map((member, index) => (
          <Box
            key={member.name}
            sx={{
              display: 'flex',
              ml: index === 0 ? 0 : '-10px',
              // Premier avatar au premier plan : l'ordre d'empilement est inverse.
              zIndex: FAMILY.length - index,
              borderRadius: '50%',
              border: '2.5px solid',
              borderColor: 'tokens.surface',
            }}
          >
            <Avatar name={member.name} tone={member.tone} size={34} />
          </Box>
        ))}
      </Box>
      <Typography sx={{ fontSize: 13, fontWeight: fontWeights.extraBold, color: 'tokens.accentPinkStrong' }}>
        +2
      </Typography>
    </Box>

    <Box
      sx={{
        ...cardSx,
        right: 70,
        bottom: 150,
        flexDirection: 'column',
        gap: 1,
        p: 2,
        borderRadius: `${String(radius.card + 2)}px`,
      }}
    >
      <Typography sx={caption}>Cette semaine</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: fontWeights.extraBold, lineHeight: '28px' }}>1,2 L</Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: 46 }}>
        {BAR_HEIGHTS.map((height, index) => (
          <Box
            key={index}
            sx={{
              width: 14,
              height,
              borderRadius: '4px',
              bgcolor: index === BAR_HEIGHTS.length - 1 ? 'tokens.primary' : (theme) => alpha(theme.palette.tokens.primary, 0.25),
            }}
          />
        ))}
      </Box>
    </Box>
  </Box>
);
